import {
  Card,
  CardTitle,
  CardHeader,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  ContractComparisonProps,
  ChangedField,
} from "./contract-comparison.types";
import { Textarea } from "@/components/ui/textarea";
import { formatInTimeZone } from "date-fns-tz";

interface FieldConfig {
  key: keyof ContractComparisonProps["initialContractData"];
  label: string;
  fieldType: "number" | "object" | "string" | "date";
}

export const ContractComparison = ({
  initialContractData,
  updatedContractData,
}: ContractComparisonProps) => {
  const companyFieldsToCompare: FieldConfig[] = [
    { key: "dealId", label: "Deal ID (hubspot)", fieldType: "number" },
    { key: "packageId", label: "Pacote", fieldType: "number" },
    {
      key: "contractResponsibleUser",
      label: "Pessoa responsável pelo contrato (cliente)",
      fieldType: "object",
    },
    {
      key: "roomResponsibleUser",
      label: "Pessoa responsável pela gestão das salas (cliente)",
      fieldType: "object",
    },
  ];

  const bookingFieldsToCompare: FieldConfig[] = [
    {
      key: "poType",
      label: "Tipo de escritório",
      fieldType: "string",
    },
    { key: "members", label: "Número de pessoas", fieldType: "number" },
    { key: "startDate", label: "Data de início", fieldType: "date" },
    { key: "endDate", label: "Data de término", fieldType: "date" },
    { key: "monthlyCredits", label: "Créditos mensais", fieldType: "number" },
    {
      key: "takeRatePercentage",
      label: "Porcentagem de Take",
      fieldType: "number",
    },
    { key: "repass", label: "Repasse", fieldType: "number" },
    { key: "partnerValue", label: "Valor do parceiro", fieldType: "string" },
  ];

  const noticeFieldsToCompare: FieldConfig[] = [
    {
      key: "noticeInDaysClient",
      label: "Período de Aviso Prévio do Cliente",
      fieldType: "number",
    },
    {
      key: "noticeInDaysPartner",
      label: "Período de Aviso Prévio do Parceiro",
      fieldType: "number",
    },
    {
      key: "expectedDurationMonths",
      label: "Vigência prevista (em meses)",
      fieldType: "number",
    },
    {
      key: "renewalIndicator",
      label: "Índice de reajuste",
      fieldType: "string",
    },
  ];

  const documentFieldsToCompare: FieldConfig[] = [
    {
      key: "acceptanceTermFiles",
      label: "Termo de aceite",
      fieldType: "object",
    },
    {
      key: "knowledgeTermFiles",
      label: "Termo de conhecimento",
      fieldType: "object",
    },
  ];

  const notesFieldsToCompare: FieldConfig[] = [
    {
      key: "notes",
      label: "Notas",
      fieldType: "string",
    },
  ];

  const getChangedFields = (fieldsToCompare: FieldConfig[]): ChangedField[] => {
    const changedFields = fieldsToCompare
      .map((fieldConfig) => {
        const initialValue = initialContractData[fieldConfig.key];
        const updatedValue = updatedContractData[fieldConfig.key];

        switch (fieldConfig.fieldType) {
          case "object": {
            const initialObject = initialValue as
              | { name?: string; id?: number }
              | undefined;
            const updatedObject = updatedValue as
              | { name: string; id: number }
              | undefined;

            const initialObjectId = Number(initialObject?.id);
            const updatedObjectId = Number(updatedObject?.id);

            if (initialObjectId !== updatedObjectId && updatedObjectId) {
              return {
                label: fieldConfig.label,
                oldValue: initialObject?.name || "",
                newValue: updatedObject?.name || "",
              };
            }
            break;
          }

          case "number": {
            const initialNum = Number(initialValue);
            const updatedNum = Number(updatedValue);

            if (initialNum !== updatedNum) {
              return {
                label: fieldConfig.label,
                oldValue: initialValue?.toString() || "",
                newValue: updatedValue?.toString() || "",
              };
            }
            break;
          }

          case "date": {
            if (initialValue !== updatedValue && updatedValue) {
              const initialDate = initialValue
                ? formatInTimeZone(initialValue as string, "UTC", "dd/MM/yyyy")
                : "";

              const updatedDate = updatedValue
                ? formatInTimeZone(updatedValue as string, "UTC", "dd/MM/yyyy")
                : "";

              return {
                label: fieldConfig.label,
                oldValue: initialDate,
                newValue: updatedDate,
              };
            }
            break;
          }

          case "string":
          default: {
            if (initialValue !== updatedValue && updatedValue) {
              return {
                label: fieldConfig.label,
                oldValue: initialValue?.toString() || "",
                newValue: updatedValue?.toString() || "",
              };
            }
            break;
          }
        }
      })
      .filter((field): field is ChangedField => field !== undefined);

    return changedFields;
  };

  const changedCompanyFields = getChangedFields(companyFieldsToCompare);
  const changedBookingFields = getChangedFields(bookingFieldsToCompare);
  const changedNoticeFields = getChangedFields(noticeFieldsToCompare);
  const changedDocumentFields = getChangedFields(documentFieldsToCompare);
  const changedNotesFields = getChangedFields(notesFieldsToCompare);

  if (
    !changedCompanyFields.length &&
    !changedBookingFields.length &&
    !changedNoticeFields.length &&
    !changedDocumentFields.length &&
    !changedNotesFields.length
  ) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="self-stretch text-foreground text-2xl font-medium">
          Resumo de edições
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground leading-tight pb-2 whitespace-nowrap">
          Confira o que foi alterado.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="inline-flex gap-8 w-full">
          {!!changedCompanyFields.length && (
            <div className="flex-1 inline-flex flex-col">
              <div className="py-3 inline-flex items-center">
                <div className="flex-1 justify-start text-foreground text-xl font-semibold leading-7">
                  Empresa Contratante
                </div>
              </div>
              {changedCompanyFields.map((field, index) => (
                <div key={index} className="py-3 inline-flex gap-2">
                  <div className="flex-1 inline-flex flex-col gap-1">
                    <div className="text-muted-foreground">{field.label}</div>
                    <div>
                      <div className="flex items-center gap-1 min-w-0">
                        <span className="text-muted-foreground font-medium line-through leading-relaxed truncate">
                          {field.oldValue}
                        </span>
                        <span className="font-medium leading-relaxed truncate">
                          → {field.newValue}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!!changedCompanyFields.length &&
            (!!changedBookingFields.length ||
              !!changedNoticeFields.length ||
              !!changedDocumentFields.length) && (
              <div className="w-px self-stretch bg-border border" />
            )}
          {!!changedBookingFields.length && (
            <div className="flex-1 inline-flex flex-col">
              <div className="py-3 inline-flex items-center">
                <div className="flex-1 justify-start text-foreground text-xl font-semibold leading-7">
                  Dados da Reserva
                </div>
              </div>
              {changedBookingFields.map((field, index) => (
                <div key={index} className="py-3 inline-flex gap-2">
                  <div className="flex-1 inline-flex flex-col gap-1">
                    <div className="text-muted-foreground">{field.label}</div>
                    <div>
                      <div className="flex items-center gap-1 min-w-0">
                        <span className="text-muted-foreground font-medium line-through leading-relaxed truncate">
                          {field.oldValue}
                        </span>
                        <span className="font-medium leading-relaxed truncate">
                          → {field.newValue}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!!changedBookingFields.length &&
            (!!changedNoticeFields.length ||
              !!changedDocumentFields.length) && (
              <div className="w-px self-stretch bg-border border" />
            )}
          {(!!changedNoticeFields.length ||
            !!changedDocumentFields.length ||
            !!changedNotesFields.length) && (
            <div className="flex-1 inline-flex flex-col">
              {!!changedNoticeFields.length && (
                <div className="flex-1 inline-flex flex-col">
                  <div className="py-3 inline-flex items-center">
                    <div className="flex-1 justify-start text-foreground text-xl font-semibold leading-7">
                      Avisos e ajustes
                    </div>
                  </div>
                  {changedNoticeFields.map((field, index) => (
                    <div key={index} className="py-3 inline-flex gap-2">
                      <div className="flex-1 inline-flex flex-col gap-1">
                        <div className="text-muted-foreground">
                          {field.label}
                        </div>
                        <div>
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground font-medium line-through leading-relaxed truncate">
                              {field.oldValue}
                            </span>
                            <span className="font-medium leading-relaxed truncate">
                              → {field.newValue}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!!changedDocumentFields.length && (
                <div className="flex-1 inline-flex flex-col">
                  <div className="py-3 inline-flex items-center">
                    <div className="flex-1 justify-start text-foreground text-xl font-semibold leading-7">
                      Documentos
                    </div>
                  </div>
                  {changedDocumentFields.map((field, index) => (
                    <div key={index} className="py-3 inline-flex gap-2">
                      <div className="flex-1 inline-flex flex-col gap-1">
                        <div className="text-muted-foreground">
                          {field.label}
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="max-w-80 flex items-center gap-2 py-2 px-4 border rounded-md">
                            <p className="text-sm text-muted-foreground overflow-hidden text-ellipsis whitespace-nowrap">
                              {field.newValue}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!!changedNotesFields.length && (
                <div className="flex-1 inline-flex flex-col">
                  <div className="py-3 inline-flex items-center">
                    <div className="flex-1 justify-start text-foreground text-xl font-semibold leading-7">
                      Notas
                    </div>
                  </div>
                  {changedNotesFields.map((field, index) => (
                    <div key={index} className="inline-flex gap-2">
                      <div className="flex-1 inline-flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                          <Textarea
                            className="h-32 bg-white ring-inset text-muted-foreground"
                            value={field.newValue}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
